import Icon from '@tiger/icons';
import { FC } from 'react';
import { FormControl, FormLabel } from 'react-bootstrap';
import { $Formutil, $Listutil, connect, EasyField } from 'react-bootstrap-formutil';
import Button from 'components/base/Button';
import { getFullName } from 'components/forms/RealNameInfo';
import Toast from 'components/Toast';
import deleteIcon from 'static/images/icon/delete.svg';
import editIcon from 'static/images/icon/edit.svg';
import { BeneficiaryTypeEnum } from 'types/customer';
import { openBeneficiaryModal } from '../Content';
import { BeneficiaryDataItem, BeneficiaryInfoFormutil } from '../type';
import styles from './style.scss';

interface ItemProps {
    type: BeneficiaryTypeEnum;
    primaryBeneficiaryInfo: BeneficiaryDataItem[]; // 主受益人信息
    secondaryBeneficiaryInfo: BeneficiaryDataItem[]; // 次受益人信息
    maxNumber: number;
    name: keyof BeneficiaryInfoFormutil;
    // 开第一个户的时候的需要比对一下, 受益人的税号不能跟账户主人的税号一样, 所以这里要传入账户主人的税号
    masterTaxNumber?: string;
    $formutil: $Formutil<BeneficiaryInfoFormutil>;
}

const BeneficiaryItem: FC<ItemProps> = ({
    type,
    primaryBeneficiaryInfo,
    secondaryBeneficiaryInfo,
    maxNumber,
    name,
    masterTaxNumber,
    $formutil
}) => {
    const isPrimary = type === BeneficiaryTypeEnum.PRIMARY;
    const isSecondary = type === BeneficiaryTypeEnum.SECONDARY;
    const { $weakParams } = $formutil.$new();
    const curBeneficiaryInfo = $weakParams[name] || [];
    const curTotalPercentage = curBeneficiaryInfo.reduce((total, item) => total + Number(item.percentage), 0);

    return (
        <EasyField<BeneficiaryDataItem[]>
            name={name}
            type="list"
            checker={value => {
                const totalPercentage = value.reduce((total, item) => total + Number(item.percentage), 0);

                if (isPrimary) {
                    if (value.length === 0) {
                        return __('您必须至少添加一位主要受益人');
                    }

                    // value中有值且总和不为100时, 返回错误信息
                    if (value.length > 0 && totalPercentage !== 100) {
                        return __('主要受益人份额占比之和需要为100%');
                    }
                } else {
                    // value中有值且总和不为100时, 返回错误信息
                    if (value.length > 0 && totalPercentage !== 100) {
                        return __('次要受益人份额占比之和需要为100%');
                    }
                }

                return true;
            }}>
            {($listutil: $Listutil<BeneficiaryDataItem>) => {
                const { percentage } = $listutil.$params;
                const fullName = getFullName($listutil.$params);
                const handleAddBeneficiary = () => {
                    // 如果是操作次要受益人, 则必须主要受益人有值才可以添加
                    if (isSecondary) {
                        if (primaryBeneficiaryInfo.length === 0) {
                            Toast.show(__('您必须至少添加一位主要受益人'));

                            return;
                        }
                    }

                    openBeneficiaryModal({
                        beneficiaryType: type,
                        primaryBeneficiaryInfo,
                        secondaryBeneficiaryInfo,
                        masterTaxNumber
                    })
                        .result.then(result => {
                            let isEmpty = false;

                            if (!fullName) {
                                isEmpty = true;
                            }

                            // 不超过最大上传数目的时候, 可以添加
                            if ($listutil.$length <= maxNumber) {
                                $listutil.$push(result);
                                /**
                                 * 因为listutil无法删除为0, 且始终会存在一个空项目, 所以这里向listutil中添加完新的数据之后, 把当前空项删除
                                 * 但也需要注意, 只能在当前项目式空项目的时候才需要删除
                                 */
                                isEmpty && $listutil.$remove($listutil.$index);
                            }
                        })
                        .catch(() => {});
                };
                const handleEditBeneficiary = () => {
                    openBeneficiaryModal({
                        beneficiaryType: type,
                        primaryBeneficiaryInfo,
                        secondaryBeneficiaryInfo,
                        defaultValues: $listutil.$params,
                        filterIndex: $listutil.$index,
                        masterTaxNumber
                    })
                        .result.then(result => {
                            /**
                             * 在指定位置插入数据, 然后删除因为数据插入后被后移一位的原数据
                             * 这里一定要先插入,再删除, 先删除后插入会有问题, 比如:
                             * 如果listutil中如果只剩下一个元素的时候, 删除后插入, 会导致插入后表单中拿不到插入后的值
                             */
                            $listutil.$insert($listutil.$index, result);
                            $listutil.$remove($listutil.$index + 1);
                        })
                        .catch(() => {});
                };
                const handleDeletePrimaryBeneficiary = () => {
                    $listutil.$remove($listutil.$index);
                };

                return (
                    <div>
                        {/* temporaryFullName和temporaryPercentage都有值时，显示姓名和百分比 */}
                        {fullName && percentage ? (
                            <div>
                                <div className={styles.infoItem}>
                                    <div className={styles.labelContainer}>
                                        <FormLabel className="mb-0">{`${__('Name')} ${
                                            $listutil.$index + 1
                                        }`}</FormLabel>
                                        <div className={styles.iconContainer}>
                                            <img
                                                onClick={handleEditBeneficiary}
                                                className={styles.icon}
                                                src={editIcon}
                                                alt="edit"
                                                title={__('Edit')}
                                            />
                                            <img
                                                onClick={handleDeletePrimaryBeneficiary}
                                                className={styles.icon}
                                                src={deleteIcon}
                                                alt="delete"
                                                title={__('Delete')}
                                            />
                                        </div>
                                    </div>
                                    <FormControl value={fullName} disabled />
                                </div>
                                <div className={styles.infoItem}>
                                    <FormLabel>{__('Percentage')}</FormLabel>
                                    <FormControl value={percentage} disabled />
                                </div>
                            </div>
                        ) : null}
                        {$listutil.$isLast() && $listutil.$length < maxNumber && curTotalPercentage !== 100 && (
                            <Button ghost type="dark" className={styles.addBtn} onClick={handleAddBeneficiary}>
                                <Icon className={styles.addIcon} type="plusCircle" />
                                {__('Add Beneficiary')}
                            </Button>
                        )}
                    </div>
                );
            }}
        </EasyField>
    );
};

export default connect(BeneficiaryItem);
